import React, { createContext, useContext, useState, useCallback } from 'react';

const PositionCloseContext = createContext();

export const usePositionClose = () => {
  const context = useContext(PositionCloseContext);
  return context;
};

export const PositionCloseProvider = ({ children }) => {
  const [closingPositions, setClosingPositions] = useState(new Set());
  const [confirmRequest, setConfirmRequest] = useState(null);

  // Check if a position is currently being closed
  const isPositionClosing = useCallback((symbol, accountId) => {
    const positionKey = `${symbol}_${accountId}`;
    return closingPositions.has(positionKey);
  }, [closingPositions]);

  // Mark a position as being closed (returns false if already closing)
  const startClosingPosition = useCallback((symbol, accountId, source = 'unknown') => {
    const positionKey = `${symbol}_${accountId}`;
    
    if (closingPositions.has(positionKey)) {
      console.warn(`⚠️ Position ${positionKey} is already being closed, ignoring duplicate close request from ${source}`);
      return false; // Already closing
    }

    // Starting position close
    setClosingPositions(prev => new Set([...prev, positionKey]));
    return true; // Successfully marked as closing
  }, [closingPositions]);

  // Mark a position as no longer being closed
  const finishClosingPosition = useCallback((symbol, accountId, success = true) => {
    const positionKey = `${symbol}_${accountId}`;
    // Finished closing position
    setClosingPositions(prev => {
      const newSet = new Set(prev);
      newSet.delete(positionKey);
      return newSet;
    });
  }, []);

  // Clear all closing positions (useful for cleanup)
  const clearAllClosingPositions = useCallback(() => {
    console.log('🧹 Clearing all closing position flags');
    setClosingPositions(new Set());
  }, []);

  // Prompt for confirmation before closing a position
  // data: { symbol, accountId, quantity, message, onConfirm, onCancel }
  const requestCloseConfirmation = useCallback((data) => {
    setConfirmRequest({ ...data });
  }, []);

  const closeConfirmation = useCallback(() => {
    setConfirmRequest(null);
  }, []);

  const value = {
    isPositionClosing,
    startClosingPosition,
    finishClosingPosition,
    clearAllClosingPositions,
    closingPositions: Array.from(closingPositions),
    confirmRequest,
    requestCloseConfirmation,
    closeConfirmation
  };

  return (
    <PositionCloseContext.Provider value={value}>
      {children}
    </PositionCloseContext.Provider>
  );
}; 